import type { DeployedUnit, Deployment, Phase } from "../api/types";

/** Cadrage de la carte sur le dispositif au premier affichage des unités, comme l'iPad (MapLibreMapView :
 *  updateMapContext arme le recentrage, consumeRecenterIfPending l'applique) : barycentre de toutes les unités
 *  AMI et ENI, au zoom d'entrée de l'iPad ; en CENTAC, l'emprise du camp. */

/** Barycentre (moyenne des longitudes et des latitudes, ChatController.calculateCenterOfGravity) des unités
 *  AMI et ENI visibles au temps `phase` ; null s'il n'y en a aucune. */
export function unitsCentroid(units: DeployedUnit[] | undefined, phase: Phase): [number, number] | null {
  const points = (units ?? [])
    .filter((u) => (u.side === "AMI" || u.side === "ENI") && !u.hidden)
    .map((u) => u.positions[phase])
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  if (!points.length) return null;
  const lon = points.reduce((sum, p) => sum + p.lon, 0) / points.length;
  const lat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  return [lon, lat];
}

/** Zoom d'entrée d'une division (registre L11) : l'iPad passe à 9 pour son fuseau de 60 km, qui déborde le
 *  cadrage standard ; le fuseau du mémento a de nouveau 60 km depuis le 14/10/2026 (8,5 pour les 90 km
 *  d'avant). `fittingZoom` redescend si les unités ne tiennent pas dans la carte. */
export const DIVISION_ENTRY_ZOOM = 9;

/** Zoom d'entrée d'un GTIA d'infanterie (registre L11) : son fuseau du mémento (8 km au lieu des 18,15 km de
 *  l'iPad) serre les S/GTIA, dont les symboles se chevauchent au zoom 10 ; une moitié de niveau de plus les
 *  sépare. `fittingZoom` redescend si les unités ne tiennent pas dans la carte. */
export const REGIMENT_INF_ENTRY_ZOOM = 10.5;

/** Zoom d'entrée de l'iPad (MapLibreMapView.entryZoom) : 10, et une moitié de niveau de moins pour un régiment
 *  d'infanterie qui freine ou escorte un convoi ; sur le web, `REGIMENT_INF_ENTRY_ZOOM` pour un GTIA d'infanterie
 *  (moitié de niveau de moins pour les mêmes missions), `DIVISION_ENTRY_ZOOM` pour une division. */
/** Zoom d'entrée d'une compagnie ou d'un escadron (docs/21 §3) : zone de 3 km (infanterie) ou de 5 km (blindé),
 *  le tiers environ de celle du GTIA ; un niveau et demi de plus qu'au GTIA d'infanterie, un de moins pour le blindé.
 *  `fittingZoom` redescend si les unités ne tiennent pas dans la carte. */
export const COMPANY_ENTRY_ZOOM = 12;
/** Zoom d'entrée d'un corps d'armée (docs/08 §23) : fuseau de 300 km sur 600 km de profondeur, trois niveaux de moins
 *  que la division. `fittingZoom` redescend si les unités ne tiennent pas dans la carte. */
export const CORPS_ENTRY_ZOOM = 6;
export const COMPANY_ARMOR_ENTRY_ZOOM = 11.5;

export function entryZoom(deployment: Pick<Deployment, "level" | "level_key" | "mission">): number {
  if (deployment.level === "DIVISION") return DIVISION_ENTRY_ZOOM;
  if (deployment.level === "CORPS") return CORPS_ENTRY_ZOOM;
  if (deployment.level === "COMPAGNIE")
    return deployment.level_key === "COMPAGNIE_ARMOR" ? COMPANY_ARMOR_ENTRY_ZOOM : COMPANY_ENTRY_ZOOM;
  if (deployment.level === "REGIMENT" && deployment.level_key === "REGIMENT_INF") {
    const wide = deployment.mission === "Freiner" || deployment.mission === "Escorte de convoi";
    return wide ? REGIMENT_INF_ENTRY_ZOOM - 0.5 : REGIMENT_INF_ENTRY_ZOOM;
  }
  return 10;
}

// Projection de Mercator de la carte, en pixels au zoom 0 (tuiles de 512 pixels).
const WORLD = 512;
const mercatorX = (lon: number) => ((lon + 180) / 360) * WORLD;
const mercatorY = (lat: number) => {
  const r = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * WORLD;
};

/** Zoom de cadrage : celui de l'iPad (`entry`) quand toutes les unités AMI et ENI tiennent autour du
 *  barycentre `center` dans la zone libre de `width` × `height` pixels (marge pour les symboles et leurs
 *  désignations) ; sinon le zoom qui les y fait tenir — la carte du web, panneaux ouverts, est plus étroite que
 *  l'écran de l'iPad. Jamais en dessous de 6. */
export function fittingZoom(
  units: DeployedUnit[] | undefined,
  phase: Phase,
  center: [number, number],
  width: number,
  height: number,
  entry: number,
  margin = 56,
): number {
  let dx = 0;
  let dy = 0;
  for (const u of units ?? []) {
    const p = u.positions[phase];
    if (!p || u.hidden || (u.side !== "AMI" && u.side !== "ENI")) continue;
    dx = Math.max(dx, Math.abs(mercatorX(p.lon) - mercatorX(center[0])));
    dy = Math.max(dy, Math.abs(mercatorY(p.lat) - mercatorY(center[1])));
  }
  const fit = (half: number, extent: number) => (extent > 0 ? Math.log2(Math.max(half - margin, 1) / extent) : entry);
  return Math.max(6, Math.min(entry, fit(width / 2, dx), fit(height / 2, dy)));
}
